/**
 * Keeps the order preview next to the buy button and in step with what the
 * shopper has typed.
 *
 * The button lives inside the form the personalisation app injects, so the
 * panel is rendered by Liquid further up and moved into place here, the same
 * way the quantity selector is. The variant image and option names come from
 * Liquid and refresh on their own, because Dawn re-renders this section on
 * every variant change; only the typed names need watching.
 */
(() => {
  const SELECTOR_PREVIEW = '[data-order-preview]';
  const SELECTOR_BUTTONS = '.product-form__buttons';
  const SELECTOR_NAMES = '[data-order-preview-names]';
  const TIMEOUT_MS = 6000;

  /** Text fields the app renders for the personalisation steps. */
  const nameFields = () =>
    Array.from(document.querySelectorAll('.gpo-app input[type="text"], .gpo-app textarea'));

  const place = () => {
    const preview = document.querySelector(SELECTOR_PREVIEW);
    if (!preview) return true; // Setting is off, or this template has no buy button.
    if (preview.dataset.placed === 'true') return true;

    const buttons = document.querySelector(SELECTOR_BUTTONS);
    if (!buttons || !buttons.parentElement) return false;

    buttons.parentElement.insertBefore(preview, buttons);
    preview.dataset.placed = 'true';
    return true;
  };

  const render = () => {
    const target = document.querySelector(SELECTOR_NAMES);
    if (!target) return;

    const values = nameFields()
      .map((field) => field.value.trim())
      .filter(Boolean);

    if (!values.length) {
      // Cleared fields must clear the line too, or the last names typed stay
      // on show after they have been deleted.
      target.textContent = '';
      target.classList.remove('order-preview__names--filled');
      return;
    }

    target.textContent = values.join(' & ');
    target.classList.add('order-preview__names--filled');
  };

  const watch = () => {
    document.addEventListener('input', (event) => {
      if (event.target.closest('.gpo-app')) render();
    });
  };

  /**
   * The app renders its display-only blocks (a closing pitch, a divider, a
   * hint) after the last field, which pushed the buy button ~400px below the
   * final name box. Those blocks carry no form control, so they can sit under
   * the button instead. They do carry the app's conditional-display
   * attributes, so they stay inside the same form and keep their order.
   */
  const SELECTOR_GROUPS = '.gpo-group__childs';
  const SELECTOR_ELEMENT = '.gpo-element';

  const liftBuyButton = () => {
    const groups = document.querySelector(SELECTOR_GROUPS);
    const buttons = document.querySelector(SELECTOR_BUTTONS);
    if (!groups || !buttons) return false; // App has not rendered yet.
    if (groups.dataset.tailMoved === 'true') return true;

    const children = Array.from(groups.querySelectorAll(':scope > ' + SELECTOR_ELEMENT));
    let lastField = -1;
    children.forEach((el, i) => {
      if (el.querySelector('input, textarea, select')) lastField = i;
    });
    if (lastField < 0) return false;

    const tail = children.slice(lastField + 1);
    // Only move blocks that hold nothing the cart form needs to submit.
    if (tail.some((el) => el.querySelector('input, textarea, select, [name]'))) return true;

    const anchor = document.querySelector('.product__tax') || buttons;
    // Reverse so the blocks keep their original order once inserted.
    tail.slice().reverse().forEach((el) => anchor.parentElement.insertBefore(el, anchor.nextSibling));
    groups.dataset.tailMoved = 'true';
    return true;
  };

  const start = () => {
    // Both jobs wait on markup the app injects, so neither one finishing is
    // reason to stop watching for the other.
    const settled = () => {
      const placed = place();
      const lifted = liftBuyButton();
      render();
      return placed && lifted;
    };

    const ready = settled();
    watch();
    if (ready) return;

    const observer = new MutationObserver(() => {
      if (settled()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), TIMEOUT_MS);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
